import React from "react";
import { AppShellTrigger, Box, Button, Icon, useIsMobile } from "@salestrak/ui";

import { CurrentUserButton } from "#app/components/filters/current-user-button";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { useGlobalSearchActions } from "#app/components/global-search/global-search-context";
import { MapControlButton } from "./map-control-button";
import { MapStylePopover } from "./map-style-popover";
import { MapZoomControls } from "./map-zoom-controls";
import {
  MIN_ZOOM,
  MAX_ZOOM,
  getMapColors,
  type MapSettings,
} from "./map-styles";
import type { Omittable } from "@salestrak/ui";

// Floating chips sit over live map imagery, so their surface color comes
// pre-resolved from the map style rather than from a theme token. Safe-area
// insets are read straight from CSS (`env()`).
const CHIP_SHADOW = "shadow-lg/5";
const TOP_INSET = "top-[calc(env(safe-area-inset-top,0px)+1rem)]";
const BOTTOM_INSET = "bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)]";

type MapOverlayControlsProps = Omittable<{
  isDark: boolean;
  // Filter
  onFilterClick?: () => void;
  onFilterClear?: () => void;
  filterCount?: number;
  // Avatar toggle
  currentUserSelected: boolean;
  onCurrentUserClick: () => void;
  onCurrentUserClear: () => void;
  // Zoom
  onZoomIn: () => void;
  onZoomOut: () => void;
  zoomLevel: number;
  // Location
  onLocationClick?: () => void;
  // Map style settings
  mapSettings: MapSettings;
  onMapSettingsChange: (settings: MapSettings) => void;
}>;

export function MapOverlayControls({
  isDark,
  onFilterClick,
  onFilterClear,
  filterCount = 0,
  currentUserSelected,
  onCurrentUserClick,
  onCurrentUserClear,
  onZoomIn,
  onZoomOut,
  zoomLevel,
  onLocationClick,
  mapSettings,
  onMapSettingsChange,
}: MapOverlayControlsProps) {
  const { controlBg, controlIcon } = getMapColors(isDark);
  const isMobile = useIsMobile();
  const { openSearch } = useGlobalSearchActions();

  return (
    // Full-map overlay: drags pass through to the map; the controls stay
    // interactive. No single class expresses both, so the whole layer is inert
    // and each interactive cluster re-arms itself with `pointer-events-auto`.
    <Box
      pointerEvents="none" bottom={0}
      left={0}
      position="absolute"
      right={0}
      top={0}
    >
      {/* The map renders no page header, and on mobile the site header rides
        * inside the nav drawer — so the left-hand chip carries the drawer
        * trigger and the search entry point. Desktop needs neither (the
        * pinned SiteHeader has both); its controls stay right. */}
      <Box
        align="center"
        className={`${TOP_INSET} pointer-events-none`}
        direction="row"
        gap="sm"
        justify={isMobile ? "between" : "end"}
        left={16}
        position="absolute"
        right={16}
      >
        {isMobile && (
          <Box
            align="center"
            className={`${CHIP_SHADOW} pointer-events-auto`}
            direction="row"
            gap="xs"
            height={40}
            paddingX="sm"
            rounded="lg"
            style={{ backgroundColor: controlBg }}
          >
            <AppShellTrigger />
            <Button
              aria-label="Search everything"
              onClick={openSearch}
              size="icon"
              variant="ghost"
            >
              <Icon color="muted" name="search" size="sm" />
            </Button>
          </Box>
        )}
        <Box
          align="center"
          className={`${CHIP_SHADOW} pointer-events-auto`}
          direction="row"
          gap="xs"
          height={40}
          paddingX="sm"
          rounded="lg"
          style={{ backgroundColor: controlBg }}
        >
          <CurrentUserButton
            onClearClick={onCurrentUserClear}
            onClick={onCurrentUserClick}
            selected={currentUserSelected}
            size="xs"
          />

          <FilterToggle
            count={filterCount}
            icon="filter"
            iconTintColor={controlIcon}
            onClear={onFilterClear}
            onClick={onFilterClick}
            size="xs"
          />

          <MapStylePopover
            onSettingsChange={onMapSettingsChange}
            settings={mapSettings}
          />
        </Box>
      </Box>

      <Box
        align="end"
        className={`${BOTTOM_INSET} pointer-events-auto`}
        gap="sm"
        position="absolute"
        right={16}
      >
        <MapControlButton isDark={isDark}>
          <Button
            aria-label="Center on my location"
            // Square hover wash — the control card clips it to its radius.
            className="rounded-none before:rounded-none"
            onClick={onLocationClick}
            size="icon-lg"
            variant="ghost"
          >
            <Icon color="muted" name="navigation" size="lg" />
          </Button>
        </MapControlButton>

        <MapZoomControls
          isDark={isDark}
          maxZoom={MAX_ZOOM}
          minZoom={MIN_ZOOM}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
          zoomLevel={zoomLevel}
        />
      </Box>
    </Box>
  );
}
